import { useCallback, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

import type { ConfigurationState } from "../types";
import {
    buildLocationOptions,
    getDeviceFilterFn,
    getDistinctDevices,
    getLocationOptionId,
    resolveInitialDevice,
} from "../utils";

export const useConfigurationState = (
    campaignConfig: IRTCurrentCampaignData,
    languagesList: ISupportedLanguagesAndLocale[],
): ConfigurationState => {
    const { params } = useCurrentStateAndParams<RTRawParams>();

    const scrapingConfigurations = useMemo(
        () => campaignConfig.scrapingConfigurations,
        [campaignConfig.scrapingConfigurations],
    );
    const deviceOptions = useMemo(() => getDistinctDevices(scrapingConfigurations), [scrapingConfigurations]);

    // Lazy initializers run only on mount — seed from URL params, fall back to defaults
    const [selectedDevice, setSelectedDevice] = useState<LocationDevice>(() =>
        resolveInitialDevice(params.device, deviceOptions),
    );

    const locationOptions = useMemo(
        () => buildLocationOptions(scrapingConfigurations, selectedDevice, languagesList),
        [scrapingConfigurations, selectedDevice, languagesList],
    );

    // Pre-select locations from URL; if none match, select all
    const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(() => {
        if (params.locationsIds) {
            const urlIds = new Set(params.locationsIds.split("&"));
            const validIds = locationOptions.filter((loc) => urlIds.has(loc.id)).map((loc) => loc.id);
            if (validIds.length > 0) {
                return new Set(validIds);
            }
        }
        return new Set(locationOptions.map((loc) => loc.id));
    });

    // Switching device resets locations to all available for that device
    const setDevice = useCallback(
        (device: LocationDevice) => {
            setSelectedDevice(device);
            const newLocations = buildLocationOptions(scrapingConfigurations, device, languagesList);
            setSelectedLocationIds(new Set(newLocations.map((loc) => loc.id)));
        },
        [scrapingConfigurations, languagesList],
    );

    const resolvedScrapingConfigs = useMemo(
        () =>
            scrapingConfigurations
                .filter(getDeviceFilterFn(selectedDevice))
                .filter((config) => selectedLocationIds.has(getLocationOptionId(config, selectedDevice))),
        [scrapingConfigurations, selectedDevice, selectedLocationIds],
    );

    return {
        selectedDevice,
        selectedLocationIds,
        deviceOptions,
        locationOptions,
        setDevice,
        setLocations: setSelectedLocationIds,
        resolvedScrapingConfigs,
    };
};
